import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { Category } from '../types';

interface ChatwootTag {
  name: string;
  count: number;
}

interface TagMapping {
  chatwootTag: string;
  categoryId: string;
}

interface ChatwootSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChatwootSyncModal({ isOpen, onClose, onSuccess }: ChatwootSyncModalProps) {
  const [chatwootTags, setChatwootTags] = useState<ChatwootTag[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [tagMappings, setTagMappings] = useState<TagMapping[]>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories();
      const categoriesData = response.categories || [];
      setCategories(categoriesData);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
      setCategories([]);
    }
  };

  const loadChatwootTags = async () => {
    setIsLoadingTags(true);
    try {
      const response = await apiService.getChatwootTags();
      setChatwootTags(response.tags);
      if (response.tags.length === 0) {
        toast('Nenhuma tag encontrada no BChat', { icon: 'ℹ️' });
      } else {
        toast.success(`${response.tags.length} tags encontradas no BChat`);
      }
    } catch (error: unknown) {
      console.error('Erro ao carregar tags do Chatwoot:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao carregar tags do Chatwoot';

      // Se for erro de configuração, mostrar mensagem específica
      if (errorMessage.includes('Configure') || errorMessage.includes('não configurado')) {
        toast.error('Configure o BChat na página de Integrações primeiro', { duration: 5000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoadingTags(false);
    }
  };

  const handleTagMappingChange = (chatwootTag: string, categoryId: string) => {
    setTagMappings((prev) => {
      const existing = prev.find((m) => m.chatwootTag === chatwootTag);
      if (existing) {
        if (!categoryId) {
          // Remove if no category selected
          return prev.filter((m) => m.chatwootTag !== chatwootTag);
        }
        // Update existing
        return prev.map((m) =>
          m.chatwootTag === chatwootTag ? { ...m, categoryId } : m
        );
      } else if (categoryId) {
        // Add new
        return [...prev, { chatwootTag, categoryId }];
      }
      return prev;
    });
  };

  const handleSync = async () => {
    if (tagMappings.length === 0) {
      toast.error('Selecione pelo menos uma tag para sincronizar');
      return;
    }

    setIsSyncing(true);
    try {
      const response = await apiService.syncChatwootContacts(tagMappings);
      toast.success(
        `Sincronização concluída! ${response.imported} contatos importados, ${response.updated} atualizados`
      );
      onSuccess();
    } catch (error: unknown) {
      console.error('Erro ao sincronizar:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao sincronizar contatos';
      toast.error(errorMessage);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClose = () => {
    setChatwootTags([]);
    setTagMappings([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50 backdrop-blur-sm overflow-y-auto">
      <div className="bg-[#1e3a5f] rounded-2xl shadow-2xl w-full max-w-2xl border border-white/20 my-8" role="dialog" aria-labelledby="chatwoot-sync-title">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6 border-b border-white/10 pb-4">
            <div>
              <h2 id="chatwoot-sync-title" className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Oswald, sans-serif' }}>
                SINCRONIZAR COM BCHAT
              </h2>
              <p className="text-sm text-gray-300 mt-1">
                Importe contatos do BChat por tags
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
              aria-label="Fechar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Load Tags Button */}
            <div className="flex justify-between items-center bg-white/5 p-4 rounded-xl border border-white/10">
              <div>
                <h3 className="font-medium text-white text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>TAGS DO BCHAT</h3>
                <p className="text-xs text-gray-400 mt-1">
                  {chatwootTags.length > 0
                    ? `${chatwootTags.length} tags encontradas`
                    : 'Clique para carregar as tags'}
                </p>
              </div>
              <button
                onClick={loadChatwootTags}
                disabled={isLoadingTags}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-900/20"
              >
                {isLoadingTags ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Carregando...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {chatwootTags.length > 0 ? 'Atualizar Tags' : 'Carregar Tags'}
                  </>
                )}
              </button>
            </div>

            {/* Tag Mappings */}
            {chatwootTags.length > 0 && (
              <div className="border border-white/10 rounded-xl overflow-hidden bg-white/5">
                <div className="bg-white/5 px-4 py-3 border-b border-white/10">
                  <div className="grid grid-cols-3 gap-4 text-xs font-medium text-gray-300 uppercase tracking-wider">
                    <div>Tag do BChat</div>
                    <div>Contatos</div>
                    <div>Categoria de Destino</div>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                  {chatwootTags.map((tag) => (
                    <div
                      key={tag.name}
                      className="px-4 py-3 border-b border-white/5 last:border-b-0 hover:bg-white/5 transition-colors"
                    >
                      <div className="grid grid-cols-3 gap-4 items-center">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-300 border border-blue-500/30">
                            {tag.name}
                          </span>
                        </div>
                        <div className="text-sm text-gray-300">
                          {tag.count} {tag.count === 1 ? 'contato' : 'contatos'}
                        </div>
                        <div>
                          <select
                            value={tagMappings.find((m) => m.chatwootTag === tag.name)?.categoryId || ''}
                            onChange={(e) => handleTagMappingChange(tag.name, e.target.value)}
                            className="w-full px-3 py-2 bg-[#1e3a5f] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 appearance-none"
                          >
                            <option value="">Não importar</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.id}>
                                {cat.nome}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instructions */}
            {chatwootTags.length === 0 && !isLoadingTags && (
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
                <h3 className="font-medium text-blue-300 text-sm mb-2 flex items-center gap-2">
                  <span className="text-lg">ℹ️</span> Como funciona
                </h3>
                <ul className="text-xs text-blue-200/80 space-y-1 ml-6 list-disc">
                  <li>Clique em "Carregar Tags" para buscar as tags do BChat</li>
                  <li>Associe cada tag do BChat a uma categoria do sistema</li>
                  <li>Os contatos das conversas com essas tags serão importados</li>
                  <li>Contatos já existentes serão atualizados</li>
                </ul>
              </div>
            )}

            {/* Selected Mappings Summary */}
            {tagMappings.length > 0 && (
              <div className="bg-green-900/20 border border-green-500/30 rounded-xl p-4">
                <h3 className="font-medium text-green-300 text-sm mb-2">
                  ✓ {tagMappings.length} {tagMappings.length === 1 ? 'tag selecionada' : 'tags selecionadas'}
                </h3>
                <div className="text-xs text-green-200/80 space-y-1">
                  {tagMappings.map((mapping) => {
                    const tag = chatwootTags.find((t) => t.name === mapping.chatwootTag);
                    const category = categories.find((c) => c.id === mapping.categoryId);
                    return (
                      <div key={mapping.chatwootTag}>
                        • <strong>{mapping.chatwootTag}</strong> ({tag?.count || 0} contatos) → {category?.nome || 'Categoria'}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 border-t border-white/10 mt-6">
            <button
              onClick={handleClose}
              className="flex-1 bg-white/5 text-gray-300 py-2 px-4 rounded-lg hover:bg-white/10 font-medium transition-colors text-sm border border-white/10"
            >
              Fechar
            </button>
            <button
              onClick={handleSync}
              disabled={tagMappings.length === 0 || isSyncing}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 font-medium transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
            >
              {isSyncing ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sincronizando...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sincronizar Contatos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
